import axios from 'axios';
import constants from 'config/constants';
import { getGlobalCurrentBatchUtils } from 'utils/commonFunc';

const APIS = {
  feeStructure: '/api/admin/transport-fee-structure', // POST, PUT / GET /school/{schoolId}/batch/{batchId} / DELETE /{id}
  area: '/api/admin/transport-area', // POST, PUT / GET /structure/{transportFeeStructId} / DELETE /{id}
  slab: '/api/admin/transport-fee-slab', // POST, PUT / GET /structure/{transportFeeStructId} / DELETE /{id}
  studentTransport: '/api/student-transport', // POST / GET /area/{transportAreaId} / DELETE /{id}
};

const BASE = constants.API_BASE_URL;

export const GET_ALL_TRANSPORT_GROUPS = 'GET_ALL_TRANSPORT_GROUPS';
export const getAllTransportGroups = transportGroups => ({ type: GET_ALL_TRANSPORT_GROUPS, transportGroups });

export const GET_TRANSPORT_AREAS = 'GET_TRANSPORT_AREAS';
export const getTransportAreas = (transportAreas, transportFeeStructId) => ({
  type: GET_TRANSPORT_AREAS,
  transportAreas,
  transportFeeStructId,
});

export const GET_TRANSPORT_SLABS = 'GET_TRANSPORT_SLABS';
export const getTransportSlabs = (transportSlabs, transportFeeStructId) => ({
  type: GET_TRANSPORT_SLABS,
  transportSlabs,
  transportFeeStructId,
});

export const GET_STUDENTS_FOR_TRANSPORT_AREA = 'GET_STUDENTS_FOR_TRANSPORT_AREA';
export const getStudentsForTransportArea = (students, transportAreaId) => ({
  type: GET_STUDENTS_FOR_TRANSPORT_AREA,
  students,
  transportAreaId,
});

// -------------------- Transport Fee Group (TransportFeeStructure) -------------------- //

export const fetchAllTransportGroupsService = data => async dispatch => {
  const { batchId = data?.batchId } = getGlobalCurrentBatchUtils();
  const { schoolId, token } = data;
  try {
    const res = await axios.get(
      `${BASE}${APIS.feeStructure}/school/${schoolId}/batch/${batchId}`,
      constants.setHeader(token)
    );
    dispatch(getAllTransportGroups(res.data));
  } catch (err) {
    console.error('err-fetchAllTransportGroupsService', err);
  }
};

export const addTransportGroupService = data => async () => {
  const { token, postData } = data;
  return axios.post(`${BASE}${APIS.feeStructure}`, postData, constants.setHeader(token));
};

export const deleteTransportGroupService = data => async () => {
  const { transportFeeStructId, token } = data;
  return axios.delete(`${BASE}${APIS.feeStructure}/${transportFeeStructId}`, constants.setHeader(token));
};

// -------------------- Route / Stop (TransportArea) -------------------- //

export const fetchTransportAreasService = data => async dispatch => {
  const { transportFeeStructId, token } = data;
  try {
    const res = await axios.get(`${BASE}${APIS.area}/structure/${transportFeeStructId}`, constants.setHeader(token));
    dispatch(getTransportAreas(res.data, transportFeeStructId));
  } catch (err) {
    console.error('err-fetchTransportAreasService', err);
  }
};

export const addTransportAreaService = data => async () => {
  const { token, postData } = data;
  return axios.post(`${BASE}${APIS.area}`, postData, constants.setHeader(token));
};

export const deleteTransportAreaService = data => async () => {
  const { transportAreaId, token } = data;
  return axios.delete(`${BASE}${APIS.area}/${transportAreaId}`, constants.setHeader(token));
};

export const fetchTransportAreaByIdService = async (transportAreaId, token) => {
  const res = await axios.get(`${BASE}${APIS.area}/${transportAreaId}`, constants.setHeader(token));
  return res.data;
};

// Plain (non-thunk) variant of fetchTransportSlabsService, for read-only spots (e.g. a student's
// own profile) that just need the slab list once and don't want to wire up the transportFee
// reducer for it.
export const fetchTransportSlabsByStructIdService = async (transportFeeStructId, token) => {
  const res = await axios.get(`${BASE}${APIS.slab}/structure/${transportFeeStructId}`, constants.setHeader(token));
  return res.data;
};

// -------------------- Fee Slabs (TransportFeeSlab) -------------------- //

export const fetchTransportSlabsService = data => async dispatch => {
  const { transportFeeStructId, token } = data;
  try {
    const res = await axios.get(`${BASE}${APIS.slab}/structure/${transportFeeStructId}`, constants.setHeader(token));
    dispatch(getTransportSlabs(res.data, transportFeeStructId));
  } catch (err) {
    console.error('err-fetchTransportSlabsService', err);
  }
};

export const addTransportSlabService = data => async () => {
  const { token, postData } = data;
  return axios.post(`${BASE}${APIS.slab}`, postData, constants.setHeader(token));
};

export const deleteTransportSlabService = data => async () => {
  const { transportFeeSlabId, token } = data;
  return axios.delete(`${BASE}${APIS.slab}/${transportFeeSlabId}`, constants.setHeader(token));
};

// -------------------- Assign students (StudentTransport) -------------------- //

export const fetchStudentsForTransportAreaService = data => async dispatch => {
  const { transportAreaId, token } = data;
  try {
    const res = await axios.get(`${BASE}${APIS.studentTransport}/area/${transportAreaId}`, constants.setHeader(token));
    dispatch(getStudentsForTransportArea(res.data, transportAreaId));
    return res.data;
  } catch (err) {
    console.error('err-fetchStudentsForTransportAreaService', err);
  }
};

// The backend only takes one StudentTransport row per call (no bulk endpoint like the generic
// fee module's assign-to-students), so assigning N students means N calls fired together.
export const saveAssignedStudentsForTransportAreaService = data => async () => {
  const { token, students, transportFeeStructId, transportAreaId, schoolId, batchId } = data;
  return Promise.all(
    students.map(student =>
      axios.post(
        `${BASE}${APIS.studentTransport}`,
        {
          studentId: student.studentId,
          transportFeeStructId,
          transportAreaId,
          calculationType: 'DISTANCE_BASED',
          schoolId,
          batchId,
        },
        constants.setHeader(token)
      )
    )
  );
};

export const deleteStudentTransportService = data => async () => {
  const { studentTransportId, token } = data;
  return axios.delete(`${BASE}${APIS.studentTransport}/${studentTransportId}`, constants.setHeader(token));
};

export const fetchStudentTransportByStudentService = async (studentId, token) => {
  const res = await axios.get(`${BASE}${APIS.studentTransport}/student/${studentId}`, constants.setHeader(token));
  return res.data;
};
